import { CURATORSHIP_RANGE_OPTIONS } from '@cannect/constants/curatorshipRanges'
import { Accordion, Form } from '@cannect/new-components/atoms'
import { Badge } from '@cannect/new-components/atoms/Badge'
import { Button } from '@cannect/new-components/atoms/Button'
import { Checkbox } from '@cannect/new-components/atoms/Form/Checkbox'
import { RangeSlider } from '@cannect/new-components/atoms/RangeSlider'
import { Typography } from '@cannect/new-components/atoms/Typography'
import { TDefaultOptionResponse } from '@cannect/services/common'
import { TProductFiltersByNames } from '@cannect/services/resources/products'
import { useEffect, useState, useMemo } from 'react'
import { LuSearch, LuSlidersHorizontal } from 'react-icons/lu'
import { useLocation } from 'react-router-dom'
import { AppliedFiltersSection } from './AppliedFiltersSection'
import { clearAllFilters, countAppliedFilters, getAppliedFilters } from './helpers'
import { useProductFilters } from './useProductFilters'

type TProductFiltersUI = ReturnType<typeof useProductFilters> & {
  filters: TProductFiltersByNames
  onFiltersVisibilityChange?: (isVisible: boolean) => void
  isFiltersVisible?: boolean
  hideToggleButton?: boolean
  showOnlyButton?: boolean
}

export const ProductFiltersUI = ({
  isLoadingFilters,
  isFetchingFilters,
  activeFiltersCount: _activeFiltersCount,
  handleFilterChange,
  clearFilters,
  isFilterOptionSelected,
  filters,
  queryParams,
  isAuthenticated,
  isPrescriberUser,
  onFiltersVisibilityChange,
  isFiltersVisible: externalIsFiltersVisible,
  hideToggleButton = false,
  showOnlyButton = false
}: TProductFiltersUI) => {
  const [brandSearchText, setBrandSearchText] = useState('')
  const [priceRange, setPriceRange] = useState<[number, number]>([10, Number(queryParams.get('max_price')) || 2500])
  const [internalIsFiltersVisible, setInternalIsFiltersVisible] = useState(true)
  const location = useLocation()
  const isBrandRoute = location.pathname.startsWith('/produtos-marca')

  // Usar estado externo se fornecido, caso contrário usar estado interno
  const isFiltersVisible = externalIsFiltersVisible !== undefined ? externalIsFiltersVisible : internalIsFiltersVisible

  const handleToggleFilters = () => {
    const newVisibility = !isFiltersVisible
    if (externalIsFiltersVisible === undefined) {
      setInternalIsFiltersVisible(newVisibility)
    }
    onFiltersVisibilityChange?.(newVisibility)
  }

  const gmpOptions =
    filters?.gmps?.length > 0
      ? filters.gmps
      : ([
          { id: 1, name: 'Farmacêutico' },
          { id: 2, name: 'Nutricional' }
        ] as TDefaultOptionResponse[])

  const curatorshipRangeOptions = CURATORSHIP_RANGE_OPTIONS

  const vehicleOptions =
    filters?.vehicles?.length > 0
      ? filters.vehicles
      : ([
          { id: 1, name: 'TCM' },
          { id: 2, name: 'Óleo de semente de cânhamo' },
          { id: 3, name: 'Óleo de coco' },
          { id: 4, name: 'Óleo de girassol' },
          //{ id: 5, name: 'Óleo de palma' },
          //{ id: 7, name: 'Azeite de oliva' },
          { id: 8, name: 'Sistemas hidrossolúveis' }
        ] as TDefaultOptionResponse[])

  const extractionMethodOptions =
    filters?.extraction_methods?.length > 0
      ? filters.extraction_methods
      : ([
          { id: 1, name: 'Alcoólica' },
          { id: 2, name: 'Alcoólica a frio' },
          { id: 3, name: 'Fluído supercrítico' },
          { id: 4, name: 'Hidrocarbonetos' }
        ] as TDefaultOptionResponse[])

  useEffect(() => {
    const maxPrice = Number(queryParams.get('max_price')) || 2500
    setPriceRange([10, maxPrice])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams.toString()])

  const handlePriceChange = (value: [number, number]) => {
    setPriceRange(value)
  }

  const handlePriceCommit = (value: [number, number]) => {
    handleFilterChange('max_price', value[1].toString())
  }

  const handleClearFilters = () => {
    setPriceRange([10, 2500])
    clearFilters()
    clearAllFilters(queryParams, location.pathname)
  }

  const handleRemoveFilter = (
    filterKey:
      | 'supplier_id'
      | 'brand_id'
      | 'shape_id'
      | 'class_id'
      | 'cannabinoid_name'
      | 'proportion_id'
      | 'translated_name'
      | 'volume_name'
      | 'max_price'
      | 'delivery_by_association'
      | 'category_slug'
      | 'gmp'
      | 'veiculo'
      | 'extraction_method'
      | 'curatorship_range'
      | 'terpene_id',
    value: string
  ) => {
    // Delegar para a API unificada do hook, que já trata especiais
    handleFilterChange(filterKey as any, value)
  }

  const filteredBrands = filters?.brands?.filter((brand) =>
    brand.name.toLowerCase().includes(brandSearchText.toLowerCase())
  )

  // Contar filtros aplicados
  const actualFiltersCount = useMemo(() => countAppliedFilters(queryParams, filters), [queryParams.toString(), filters])

  // Coletar todos os filtros aplicados
  const appliedFilters = useMemo(() => getAppliedFilters(queryParams, filters), [queryParams.toString(), filters])

  if (isLoadingFilters) {
    return (
      <div className="space-y-6">
        {[1, 2, 3, 4].map((index) => (
          <div key={index} className="space-y-3">
            <div className="h-5 w-24 animate-pulse rounded bg-gray-200" />
            <div className="flex flex-wrap gap-2">
              {[1, 2, 3].map((badgeIndex) => (
                <Badge key={badgeIndex} isLoading className="h-8 w-16" />
              ))}
            </div>
          </div>
        ))}
      </div>
    )
  }

  // Se showOnlyButton for true, renderizar apenas o botão sem container
  if (showOnlyButton) {
    return (
      <Button
        unstyled
        onClick={handleToggleFilters}
        className="flex items-center gap-2 text-gray-700 hover:text-gray-900">
        <LuSlidersHorizontal className="h-5 w-5" />
        <Typography.Text className="underline">
          {isFiltersVisible ? 'Esconder filtros' : 'Mostrar filtros'}
        </Typography.Text>
      </Button>
    )
  }

  return (
    <div className="space-y-3">
      {!hideToggleButton && (
        <Button
          unstyled
          onClick={handleToggleFilters}
          className="flex items-center gap-2 text-gray-700 hover:text-gray-900">
          <LuSlidersHorizontal className="h-5 w-5" />
          <Typography.Text className="underline">
            {isFiltersVisible ? 'Esconder filtros' : 'Mostrar filtros'}
          </Typography.Text>
        </Button>
      )}

      {isFiltersVisible && (
        <AppliedFiltersSection
          filtersCount={actualFiltersCount}
          appliedFilters={appliedFilters}
          onClearAll={handleClearFilters}
          onRemoveFilter={handleRemoveFilter}
        />
      )}

      {isFiltersVisible && isPrescriberUser && (
        <Accordion.Root type="single" collapsible defaultValue="curatorship_range">
          <Accordion.Item value="curatorship_range">
            <Accordion.Trigger className="group flex w-full items-center py-3">
              <Typography.Heading type="headingSix" className="whitespace-nowrap font-medium text-gray-900">
              Adesão à Curadoria
              </Typography.Heading>
              <span className="ml-auto mr-2 h-5 w-[42px] shrink-0 rounded-md border border-yellow-500 bg-yellow-50 text-center text-xs font-medium leading-5 text-yellow-700">
                Novo
              </span>
            </Accordion.Trigger>

            <Accordion.Content>
              <div className="flex flex-wrap gap-2" key="curatorship_range">
                {curatorshipRangeOptions.map((option) => {
                  const isSelected = isFilterOptionSelected('curatorship_range', option.id)

                  return (
                    <Badge
                      key={option.id}
                      variant={isSelected ? 'primary_medium' : 'outline'}
                      className={`cursor-pointer gap-1.5 border-gray-300 transition-all hover:opacity-80 ${isSelected ? 'text-gray-100' : 'text-gray-500 hover:border-primary-500'} `}
                      onClick={() => handleFilterChange('curatorship_range', option.id)}>
                      <span className={`size-2 shrink-0 rounded-full ${option.className}`} aria-hidden />
                      <span className="whitespace-nowrap font-medium">{option.name}</span>
                    </Badge>
                  )
                })}
              </div>
            </Accordion.Content>
          </Accordion.Item>
        </Accordion.Root>
      )}

      {isFiltersVisible && import.meta.env.VITE_APP_GENETIC_TEST_ENABLED === 'true' && (
        <Accordion.Root type="single" collapsible defaultValue="cannabis-origem">
          <Accordion.Item value="cannabis-origem">
            <Accordion.Trigger className="group flex w-full items-center py-3">
              <Typography.Heading type="headingSix" className="whitespace-nowrap font-medium text-gray-900">
                Cannabis Medicinal
              </Typography.Heading>
            </Accordion.Trigger>

            <Accordion.Content>
              <div className="flex flex-wrap gap-2">
                {[
                  { label: 'Importado (RDC 660)', filterKey: 'supplier_id', value: '1' },
                  { label: 'Nacional (RDC 327)', filterKey: 'supplier_id', value: '18' },
                  { label: 'Associações', filterKey: 'delivery_by_association', value: 'true' }
                ].map(({ label, filterKey, value }) => {
                  const isSelected =
                    filterKey === 'delivery_by_association'
                      ? queryParams.get('delivery_by_association') === 'true'
                      : isFilterOptionSelected(filterKey as any, value)

                  return (
                    <Badge
                      key={label}
                      variant={isSelected ? 'primary_medium' : 'outline'}
                      className={`cursor-pointer border-gray-300 transition-all hover:opacity-80 ${isSelected ? 'text-gray-100' : 'text-gray-500 hover:border-primary-500'}`}
                      onClick={() => handleFilterChange(filterKey as any, value)}>
                      {label}
                    </Badge>
                  )
                })}
              </div>
            </Accordion.Content>
          </Accordion.Item>
        </Accordion.Root>
      )}

      {isFiltersVisible && isPrescriberUser && gmpOptions?.length > 0 && (
        <Accordion.Root type="single" collapsible defaultValue="gmps">
          <Accordion.Item value="gmps">
            <Accordion.Trigger className="group flex w-full items-center py-3">
              <Typography.Heading type="headingSix" className="whitespace-nowrap font-medium text-gray-900">
                GMP
              </Typography.Heading>
              <span className="ml-auto mr-2 h-5 w-[42px] shrink-0 rounded-md border border-yellow-500 bg-yellow-50 text-center text-xs font-medium leading-5 text-yellow-700">
                Novo
              </span>
            </Accordion.Trigger>

            <Accordion.Content>
              <div className="max-h-80 overflow-y-auto">
                <div className="flex flex-wrap gap-2" key="gmps">
                  {gmpOptions?.map((option: TDefaultOptionResponse) => {
                    const isSelected = isFilterOptionSelected('gmp', option.name.toString())

                    return (
                      <Badge
                        key={option.name}
                        variant={isSelected ? 'primary_medium' : 'outline'}
                        className={`cursor-pointer border-gray-300 transition-all hover:opacity-80 ${isSelected ? 'text-gray-100' : 'text-gray-500 hover:border-primary-500'} `}
                        onClick={() => handleFilterChange('gmp', option.name.toString())}>
                        {option.name}
                      </Badge>
                    )
                  })}
                </div>
              </div>
            </Accordion.Content>
          </Accordion.Item>
        </Accordion.Root>
      )}

      {isFiltersVisible && isPrescriberUser && vehicleOptions?.length > 0 && (
        <Accordion.Root type="single" collapsible defaultValue="vehicles">
          <Accordion.Item value="vehicles">
            <Accordion.Trigger className="group flex w-full items-center py-3">
              <Typography.Heading type="headingSix" className="whitespace-nowrap font-medium text-gray-900">
                Veículo
              </Typography.Heading>
              <span className="ml-auto mr-2 h-5 w-[42px] shrink-0 rounded-md border border-yellow-500 bg-yellow-50 text-center text-xs font-medium leading-5 text-yellow-700">
                Novo
              </span>
            </Accordion.Trigger>

            <Accordion.Content>
              <div className="max-h-80 overflow-y-auto">
                <div className="flex flex-wrap gap-2" key="vehicles">
                  {vehicleOptions?.map((option: TDefaultOptionResponse) => {
                    const isSelected = isFilterOptionSelected('veiculo', option.name.toString())

                    return (
                      <Badge
                        key={option.name}
                        variant={isSelected ? 'primary_medium' : 'outline'}
                        className={`cursor-pointer border-gray-300 transition-all hover:opacity-80 ${isSelected ? 'text-gray-100' : 'text-gray-500 hover:border-primary-500'} `}
                        onClick={() => handleFilterChange('veiculo', option.name.toString())}>
                        {option.name}
                      </Badge>
                    )
                  })}
                </div>
              </div>
            </Accordion.Content>
          </Accordion.Item>
        </Accordion.Root>
      )}

      {isFiltersVisible && isPrescriberUser && extractionMethodOptions?.length > 0 && (
        <Accordion.Root type="single" collapsible defaultValue="extraction-methods">
          <Accordion.Item value="extraction-methods">
            <Accordion.Trigger className="group flex w-full items-center py-3">
              <Typography.Heading type="headingSix" className="whitespace-nowrap font-medium text-gray-900">
                Método de Extração
              </Typography.Heading>
              <span className="ml-auto mr-2 h-5 w-[42px] shrink-0 rounded-md border border-yellow-500 bg-yellow-50 text-center text-xs font-medium leading-5 text-yellow-700">
                Novo
              </span>
            </Accordion.Trigger>

            <Accordion.Content>
              <div className="max-h-80 overflow-y-auto">
                <div className="flex flex-wrap gap-2" key="extraction-methods">
                  {extractionMethodOptions?.map((option: TDefaultOptionResponse) => {
                    const isSelected = isFilterOptionSelected('extraction_method', option.name.toString())

                    return (
                      <Badge
                        key={option.name}
                        variant={isSelected ? 'primary_medium' : 'outline'}
                        className={`cursor-pointer border-gray-300 transition-all hover:opacity-80 ${isSelected ? 'text-gray-100' : 'text-gray-500 hover:border-primary-500'} `}
                        onClick={() => handleFilterChange('extraction_method', option.name.toString())}>
                        {option.name}
                      </Badge>
                    )
                  })}
                </div>
              </div>
            </Accordion.Content>
          </Accordion.Item>
        </Accordion.Root>
      )}

      {isFiltersVisible && !isBrandRoute && filters?.brands?.length > 0 && (
        <Accordion.Root type="single" collapsible defaultValue="brands">
          <Accordion.Item value="brands">
            <Accordion.Trigger className="flex w-full items-center justify-between py-3">
              <div className="flex items-center gap-2">
                <Typography.Heading type="headingSix" className="font-medium text-gray-900">
                  Marcas
                </Typography.Heading>
              </div>
            </Accordion.Trigger>

            <Accordion.Content>
              <div className="space-y-3 bg-transparent">
                <Form.Input
                  size="md"
                  className="h-8 rounded-full bg-white"
                  placeholder="Buscar marcas"
                  value={brandSearchText}
                  onChange={(e) => setBrandSearchText(e.target.value)}
                  variant="search"
                  rigthElement={<LuSearch />}
                />

                <div className="max-h-80 space-y-2 overflow-y-auto">
                  {filteredBrands.map((option: TDefaultOptionResponse) => {
                    const isSelected = isFilterOptionSelected('brand_id', option.id.toString())

                    return (
                      <Checkbox
                        checkboxClassName="w-5 h-5"
                        key={option.id}
                        label={option.name}
                        checked={isSelected}
                        onCheckedChange={() => handleFilterChange('brand_id', option.id.toString())}
                      />
                    )
                  })}
                </div>
              </div>
            </Accordion.Content>
          </Accordion.Item>
        </Accordion.Root>
      )}

      {isFiltersVisible && filters?.shapes?.length > 0 && (
        <Accordion.Root type="single" collapsible defaultValue="brands">
          <Accordion.Item value="brands">
            <Accordion.Trigger className="flex w-full items-center justify-between py-3">
              <div className="flex items-center gap-2">
                <Typography.Heading type="headingSix" className="font-medium text-gray-900">
                  Formas
                </Typography.Heading>
              </div>
            </Accordion.Trigger>

            <Accordion.Content>
              <div className="max-h-80 overflow-y-auto">
                <div className="flex flex-wrap gap-2" key="shapes">
                  {filters?.shapes?.map((option: TDefaultOptionResponse) => {
                    const isSelected = isFilterOptionSelected('shape_id', option.id?.toString())

                    return (
                      <Badge
                        key={option.id}
                        variant={isSelected ? 'primary_medium' : 'outline'}
                        className={`cursor-pointer border-gray-300 transition-all hover:opacity-80 ${isSelected ? 'text-gray-100' : 'text-gray-500 hover:border-primary-500'} `}
                        onClick={() => handleFilterChange('shape_id', option.id.toString())}>
                        {option.name}
                      </Badge>
                    )
                  })}
                </div>
              </div>
            </Accordion.Content>
          </Accordion.Item>
        </Accordion.Root>
      )}

      {isFiltersVisible && filters?.classes?.length > 0 && (
        <Accordion.Root type="single" collapsible defaultValue="brands">
          <Accordion.Item value="brands">
            <Accordion.Trigger className="flex w-full items-center justify-between py-3">
              <div className="flex items-center gap-2">
                <Typography.Heading type="headingSix" className="font-medium text-gray-900">
                  Espectros
                </Typography.Heading>
              </div>
            </Accordion.Trigger>

            <Accordion.Content>
              <div className="max-h-80 overflow-y-auto">
                <div className="flex flex-wrap gap-2" key="classes">
                  {filters?.classes?.map((option: TDefaultOptionResponse) => {
                    const isSelected = isFilterOptionSelected('class_id', option.id?.toString())

                    return (
                      <Badge
                        key={option.id}
                        variant={isSelected ? 'primary_medium' : 'outline'}
                        className={`cursor-pointer border-gray-300 transition-all hover:opacity-80 ${isSelected ? 'text-gray-100' : 'text-gray-500 hover:border-primary-500'} `}
                        onClick={() => handleFilterChange('class_id', option.id.toString())}>
                        {option.name}
                      </Badge>
                    )
                  })}
                </div>
              </div>
            </Accordion.Content>
          </Accordion.Item>
        </Accordion.Root>
      )}

      {isFiltersVisible && isAuthenticated && (
        <Accordion.Root type="single" collapsible defaultValue="price">
          <Accordion.Item value="price">
            <Accordion.Trigger className="flex w-full items-center justify-between py-3">
              <div className="flex items-center gap-2">
                <Typography.Heading type="headingSix" className="font-medium text-gray-900">
                  Preço
                </Typography.Heading>
              </div>
            </Accordion.Trigger>

            <Accordion.Content>
              <div className="max-h-80 overflow-y-auto px-2">
                <RangeSlider
                  max={2500}
                  step={10}
                  value={priceRange}
                  onValueChange={handlePriceChange}
                  onValueCommit={handlePriceCommit}
                />
              </div>
            </Accordion.Content>
          </Accordion.Item>
        </Accordion.Root>
      )}

      {isFiltersVisible && filters?.cannabinoids?.length > 0 && (
        <Accordion.Root type="single" collapsible defaultValue="cannabinoids">
          <Accordion.Item value="cannabinoids">
            <Accordion.Trigger className="flex w-full items-center justify-between py-3">
              <div className="flex items-center gap-2">
                <Typography.Heading type="headingSix" className="font-medium text-gray-900">
                  Canabinoides
                </Typography.Heading>
              </div>
            </Accordion.Trigger>

            <Accordion.Content>
              <div className="max-h-80 overflow-y-auto">
                <div className="flex flex-wrap gap-2" key="cannabinoids">
                  {filters?.cannabinoids?.map((option: TDefaultOptionResponse) => {
                    const isSelected = isFilterOptionSelected('cannabinoid_name', option.name.toString())

                    return (
                      <Badge
                        key={option.name}
                        variant={isSelected ? 'primary_medium' : 'outline'}
                        className={`cursor-pointer border-gray-300 transition-all hover:opacity-80 ${isSelected ? 'text-gray-100' : 'text-gray-500 hover:border-primary-500'} `}
                        onClick={() => handleFilterChange('cannabinoid_name', option.name.toString())}>
                        {option.name}
                      </Badge>
                    )
                  })}
                </div>
              </div>
            </Accordion.Content>
          </Accordion.Item>
        </Accordion.Root>
      )}

      {isFiltersVisible && isPrescriberUser && filters?.terpenes?.length > 0 && (
        <Accordion.Root type="single" collapsible defaultValue="terpenes">
          <Accordion.Item value="terpenes">
            <Accordion.Trigger className="group flex w-full items-center py-3">
              <Typography.Heading type="headingSix" className="whitespace-nowrap font-medium text-gray-900">
                Terpenos
              </Typography.Heading>
              <span className="ml-auto mr-2 h-5 w-[42px] shrink-0 rounded-md border border-yellow-500 bg-yellow-50 text-center text-xs font-medium leading-5 text-yellow-700">
                Novo
              </span>
            </Accordion.Trigger>

            <Accordion.Content>
              <div className="max-h-80 overflow-y-auto">
                <div className="flex flex-wrap gap-2" key="terpenes">
                  {filters?.terpenes?.map((option: TDefaultOptionResponse) => {
                    const isSelected = isFilterOptionSelected('terpene_id', option.id?.toString())

                    return (
                      <Badge
                        key={option.id}
                        variant={isSelected ? 'primary_medium' : 'outline'}
                        className={`cursor-pointer border-gray-300 transition-all hover:opacity-80 ${isSelected ? 'text-gray-100' : 'text-gray-500 hover:border-primary-500'} `}
                        onClick={() => handleFilterChange('terpene_id', option.id.toString())}>
                        {option.name}
                      </Badge>
                    )
                  })}
                </div>
              </div>
            </Accordion.Content>
          </Accordion.Item>
        </Accordion.Root>
      )}

      {isFiltersVisible && filters?.volumes?.length > 0 && (
        <Accordion.Root type="single" collapsible defaultValue="volumes">
          <Accordion.Item value="volumes">
            <Accordion.Trigger className="flex w-full items-center justify-between py-3">
              <div className="flex items-center gap-2">
                <Typography.Heading type="headingSix" className="font-medium text-gray-900">
                  Tamanho
                </Typography.Heading>
              </div>
            </Accordion.Trigger>

            <Accordion.Content>
              <div className="max-h-80 overflow-y-auto">
                <div className="flex flex-wrap gap-2" key="volumes">
                  {filters?.volumes?.map((option: TDefaultOptionResponse) => {
                    const isSelected = isFilterOptionSelected('volume_name', option.name.toString())

                    return (
                      <Badge
                        key={option.name}
                        variant={isSelected ? 'primary_medium' : 'outline'}
                        className={`cursor-pointer border-gray-300 transition-all hover:opacity-80 ${isSelected ? 'text-gray-100' : 'text-gray-500 hover:border-primary-500'} `}
                        onClick={() => handleFilterChange('volume_name', option.name.toString())}>
                        {option.name}
                      </Badge>
                    )
                  })}
                </div>
              </div>
            </Accordion.Content>
          </Accordion.Item>
        </Accordion.Root>
      )}

      {isFiltersVisible && filters?.proportions?.length > 0 && (
        <Accordion.Root type="single" collapsible defaultValue="proportions">
          <Accordion.Item value="proportions">
            <Accordion.Trigger className="flex w-full items-center justify-between py-3">
              <div className="flex items-center gap-2">
                <Typography.Heading type="headingSix" className="font-medium text-gray-900">
                  Proporções
                </Typography.Heading>
              </div>
            </Accordion.Trigger>

            <Accordion.Content>
              <div className="max-h-80 overflow-y-auto">
                <div className="flex flex-wrap gap-2" key="proportions">
                  {filters.proportions.map((option: TDefaultOptionResponse) => {
                    const isSelected = isFilterOptionSelected('proportion_id', option.id.toString())

                    return (
                      <Badge
                        key={option.id}
                        variant={isSelected ? 'primary_medium' : 'outline'}
                        className={`cursor-pointer border-gray-300 transition-all hover:opacity-80 ${isSelected ? 'text-gray-100' : 'text-gray-500 hover:border-primary-500'} `}
                        onClick={() => handleFilterChange('proportion_id', option.id.toString())}>
                        {option.name}
                      </Badge>
                    )
                  })}
                </div>
              </div>
            </Accordion.Content>
          </Accordion.Item>
        </Accordion.Root>
      )}

      {isFiltersVisible && isFetchingFilters && (
        <div className="flex items-center justify-center py-4">
          <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-primary-600" />
        </div>
      )}
    </div>
  )
}
